import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

// Utilidades para WebAuthn
const bufferToBase64URL = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const base64URLToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const Login = () => {
  const { login, register, API, setUser, setToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    celular: '',
    fecha_nacimiento: '',
    rol: '',  // Sin selección inicial - usuario debe elegir
    codigo_referido_usado: '',  // Código de referido opcional
    nombre_iglesia: '',  // Nombre de la iglesia
    nombre_pastor: ''  // Nombre del pastor
  });
  const [registroConfig, setRegistroConfig] = useState(null);
  const [codigoReferidoDetectado, setCodigoReferidoDetectado] = useState(null);
  const [activeTab, setActiveTab] = useState('login');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  
  // Estados para biométrico
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [loadingBiometric, setLoadingBiometric] = useState(false);

  // Cargar configuración de registro y detectar código de referido en URL
  React.useEffect(() => {
    loadRegistroConfig();
    checkBiometricSupport();
    
    // Restaurar email recordado
    const savedEmail = localStorage.getItem('remembered_email');
    if (savedEmail) {
      setLoginData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
    
    // Detectar código de referido en URL
    const urlParams = new URLSearchParams(window.location.search);
    const codigoRef = urlParams.get('ref');
    
    if (codigoRef) {
      setCodigoReferidoDetectado(codigoRef);
      setRegisterData(prev => ({
        ...prev,
        codigo_referido_usado: codigoRef
      }));
      // Auto-seleccionar tab de registro
      setActiveTab('register');
    }
  }, []);

  // Verificar si el dispositivo soporta biométrico
  const checkBiometricSupport = async () => {
    if (window.PublicKeyCredential) {
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricSupported(available);
      } catch (e) {
        setBiometricSupported(false);
      }
    }
  };

  // Verificar si el usuario tiene biométrico configurado (cuando escribe el email)
  const checkBiometricAvailable = async (email) => {
    if (!biometricSupported || !email) {
      setBiometricAvailable(false);
      return;
    }
    try {
      const response = await axios.post(`${API}/auth/webauthn/check-available`, { email });
      setBiometricAvailable(response.data.available);
    } catch (e) {
      setBiometricAvailable(false);
    }
  };

  // Login con biométrico
  const handleBiometricLogin = async () => {
    if (!loginData.email) {
      toast.error('Ingresa tu email primero');
      return;
    }

    setLoadingBiometric(true);
    try {
      // 1. Iniciar autenticación
      const startResponse = await axios.post(`${API}/auth/webauthn/authenticate/start`, {
        email: loginData.email
      });
      const options = startResponse.data.options;

      // 2. Convertir challenge a ArrayBuffer
      options.challenge = base64URLToBuffer(options.challenge);
      
      // Convertir allowCredentials
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(cred => ({
          ...cred,
          id: base64URLToBuffer(cred.id)
        }));
      }

      // 3. Obtener credencial del autenticador
      const credential = await navigator.credentials.get({
        publicKey: options
      });

      // 4. Preparar respuesta
      const credentialResponse = {
        id: credential.id,
        rawId: bufferToBase64URL(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64URL(credential.response.clientDataJSON),
          authenticatorData: bufferToBase64URL(credential.response.authenticatorData),
          signature: bufferToBase64URL(credential.response.signature),
          userHandle: credential.response.userHandle ? bufferToBase64URL(credential.response.userHandle) : null,
        }
      };

      // 5. Completar autenticación
      const authResponse = await axios.post(`${API}/auth/webauthn/authenticate/complete`, {
        email: loginData.email,
        credential: credentialResponse
      });

      // 6. Guardar token y usuario
      const { access_token, user } = authResponse.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      toast.success('🔐 ¡Bienvenido!');
      
      // Recargar la página para que App.js detecte el token y cargue el usuario
      window.location.href = '/app/dashboard';

    } catch (error) {
      console.error('Error biométrico:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Autenticación cancelada');
      } else {
        toast.error(error.response?.data?.detail || 'Error en autenticación biométrica');
      }
    } finally {
      setLoadingBiometric(false);
    }
  };

  // Cuando cambia el email, verificar si tiene biométrico
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loginData.email && loginData.email.includes('@')) {
        checkBiometricAvailable(loginData.email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [loginData.email]);

  const loadRegistroConfig = async () => {
    try {
      const response = await fetch(`${API}/registro-config`);
      if (response.ok) {
        const data = await response.json();
        setRegistroConfig(data);
      }
    } catch (error) {
      console.log('No registro config found');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    const result = await login(loginData.email, loginData.password, rememberMe);
    setLoading(false);

    if (result.success) {
      // Guardar o borrar email recordado
      if (rememberMe) {
        localStorage.setItem('remembered_email', loginData.email);
      } else {
        localStorage.removeItem('remembered_email');
      }
      
      toast.success('¡Bienvenido al Instituto!');
      
      // Check if there's a redirect URL (for QR attendance flow)
      if (result.redirect) {
        sessionStorage.removeItem('redirect_after_login');
        navigate(result.redirect.replace(window.location.origin, ''));
      } else {
        // Check if user arrived via deep link (e.g. push notification)
        // The URL will be like /app/chat-iba?canal=xxx
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;
        const deepLinkTarget = currentPath + currentSearch;
        
        if (currentPath !== '/app' && currentPath !== '/app/' && currentPath !== '/login' && currentPath.startsWith('/app/')) {
          console.log('🔗 Deep link detected, redirecting to:', deepLinkTarget);
          navigate(deepLinkTarget, { replace: true });
        } else {
          navigate('/app/dashboard');
        }
      }
    } else {
      toast.error(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.email || !registerData.password || !registerData.nombre || !registerData.apellido) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    if (!registerData.rol) {
      toast.error('Por favor selecciona tu rol');
      return;
    }

    // Iglesia y Pastor obligatorios para estudiantes
    if (registerData.rol === 'estudiante') {
      if (!registerData.nombre_iglesia || !registerData.nombre_iglesia.trim()) {
        toast.error('El campo "Iglesia" es obligatorio para estudiantes');
        return;
      }
      if (!registerData.nombre_pastor || !registerData.nombre_pastor.trim()) {
        toast.error('El campo "Pastor" es obligatorio para estudiantes');
        return;
      }
    }

    setLoading(true);
    const result = await register(registerData);
    setLoading(false);

    if (result.success) {
      if (result.requiresApproval) {
        // Mostrar mensaje especial para usuarios que requieren aprobación
        toast.success(result.message || '¡Cuenta creada! Pendiente de aprobación.', {
          duration: 8000,
        });
        // Limpiar formulario y volver a login
        setRegisterData({
          email: '',
          password: '',
          nombre: '',
          apellido: '',
          rol: '',
          nacionalidad: '',
          telefono: '',
          pais: '',
          genero: '',
          fecha_nacimiento: '',
          estado_civil: '',
          direccion: '',
          codigo_referido_usado: ''
        });
        setActiveTab('login');
      } else {
        toast.success('¡Cuenta creada exitosamente!');
      }
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src={process.env.REACT_APP_LOGO_URL || "/logo-iba.jpg"} 
            alt="Instituto Bíblico Apostólico del Uruguay" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Instituto Bíblico Apostólico</h1>
          <p className="text-lg text-gray-600">del Uruguay</p>
          <p className="text-sm text-gray-500 mt-2">est. 1978</p>
        </div>

        <Card className="glass shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl text-gradient font-semibold">Acceso al Campus</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger 
                  value="login" 
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                  data-testid="login-tab"
                >
                  Iniciar Sesión
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                  data-testid="register-tab"
                >
                  Registrarse
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="focus-instituto"
                      data-testid="login-email-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Contraseña</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="Tu contraseña"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="focus-instituto pr-10"
                        data-testid="login-password-input"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        tabIndex={-1}
                      >
                        <span>{showLoginPassword ? '🙈' : '👁️'}</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Recordarme checkbox */}
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="checkbox"
                      id="remember-me"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      data-testid="remember-me-checkbox"
                    />
                    <label htmlFor="remember-me" className="text-sm text-gray-600 cursor-pointer select-none">
                      Recordarme en este dispositivo
                    </label>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full instituto-gradient btn-hover text-white font-medium py-3 mt-4"
                    disabled={loading}
                    data-testid="login-submit-button"
                  >
                    <span className="flex items-center justify-center">
                      {loading ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                          <span>Iniciando sesión...</span>
                        </>
                      ) : (
                        <>
                          <span>Ingresar</span>
                          <i className="fas fa-arrow-right ml-2"></i>
                        </>
                      )}
                    </span>
                  </Button>
                  
                  {/* Botón de login biométrico */}
                  {biometricSupported && biometricAvailable && (
                    <div className="mt-4">
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-gray-500">o</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={handleBiometricLogin}
                        disabled={loadingBiometric || !loginData.email}
                        className="w-full mt-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-3"
                      >
                        <span className="flex items-center justify-center">
                          {loadingBiometric ? (
                            <>
                              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                              <span>Verificando...</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-fingerprint mr-2"></i>
                              <span>Acceder con huella / Face ID</span>
                            </>
                          )}
                        </span>
                      </Button>
                    </div>
                  )}

                  {/* Forgot Password Link */}
                  <div className="mt-4 text-center">
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                    >
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register">
                {codigoReferidoDetectado && (
                  <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
                    <div className="flex items-start">
                      <i className="fas fa-user-friends text-green-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <p className="text-green-800 font-semibold">¡Bienvenido por recomendación!</p>
                        <p className="text-green-700 text-sm mt-1">
                          Alguien te ha recomendado nuestro Instituto. Tu código de referido <strong>{codigoReferidoDetectado}</strong> ya está ingresado.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-nombre">Nombre</Label>
                      <Input
                        id="register-nombre"
                        placeholder="Tu nombre"
                        value={registerData.nombre}
                        onChange={(e) => setRegisterData({ ...registerData, nombre: e.target.value })}
                        className="focus-instituto"
                        data-testid="register-nombre-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-apellido">Apellido</Label>
                      <Input
                        id="register-apellido"
                        placeholder="Tu apellido"
                        value={registerData.apellido}
                        onChange={(e) => setRegisterData({ ...registerData, apellido: e.target.value })}
                        className="focus-instituto"
                        data-testid="register-apellido-input"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="focus-instituto"
                      data-testid="register-email-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-celular">Celular (opcional)</Label>
                    <Input
                      id="register-celular"
                      type="tel"
                      placeholder="+598 99 123 456"
                      value={registerData.celular}
                      onChange={(e) => setRegisterData({ ...registerData, celular: e.target.value })}
                      className="focus-instituto"
                      data-testid="register-celular-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-iglesia">
                        Nombre de tu Iglesia
                        {registerData.rol === 'estudiante' && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Input
                        id="register-iglesia"
                        placeholder="Ej: Iglesia Apostólica..."
                        value={registerData.nombre_iglesia}
                        onChange={(e) => setRegisterData({ ...registerData, nombre_iglesia: e.target.value })}
                        className="focus-instituto"
                        required={registerData.rol === 'estudiante'}
                        data-testid="register-iglesia-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-pastor">
                        Nombre de tu Pastor
                        {registerData.rol === 'estudiante' && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Input
                        id="register-pastor"
                        placeholder="Ej: Pastor Juan..."
                        value={registerData.nombre_pastor}
                        onChange={(e) => setRegisterData({ ...registerData, nombre_pastor: e.target.value })}
                        className="focus-instituto"
                        required={registerData.rol === 'estudiante'}
                        data-testid="register-pastor-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-fecha-nacimiento">Fecha de Nacimiento</Label>
                    <Input
                      id="register-fecha-nacimiento"
                      type="date"
                      value={registerData.fecha_nacimiento}
                      onChange={(e) => setRegisterData({ ...registerData, fecha_nacimiento: e.target.value })}
                      className="focus-instituto"
                      data-testid="register-fecha-nacimiento-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Contraseña</Label>
                    <div className="relative">
                      <Input
                        id="register-password"
                        type={showRegisterPassword ? "text" : "password"}
                        placeholder="Crea tu contraseña"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="focus-instituto pr-10"
                        data-testid="register-password-input"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        tabIndex={-1}
                      >
                        {showRegisterPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-codigo-referido">Código de Referido (opcional)</Label>
                    <Input
                      id="register-codigo-referido"
                      type="text"
                      placeholder="Ej: JUAN2025"
                      value={registerData.codigo_referido_usado}
                      onChange={(e) => setRegisterData({ ...registerData, codigo_referido_usado: e.target.value.toUpperCase() })}
                      className="focus-instituto"
                      data-testid="register-codigo-referido-input"
                      disabled={codigoReferidoDetectado ? true : false}
                    />
                    {codigoReferidoDetectado ? (
                      <p className="text-xs text-green-600 mt-1 font-medium">
                        <i className="fas fa-check-circle mr-1"></i>
                        Código aplicado automáticamente
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        <i className="fas fa-info-circle mr-1"></i>
                        Si alguien te recomendó el Instituto, ingresa su código
                      </p>
                    )}
                  </div>
                  <div className="space-y-3">
                    <Label>Selecciona tu rol</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {/* Opción Estudiante */}
                      <button
                        type="button"
                        onClick={() => setRegisterData({ ...registerData, rol: 'estudiante' })}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          registerData.rol === 'estudiante'
                            ? 'border-gray-800 bg-gray-100 shadow-lg ring-2 ring-gray-400'
                            : 'border-green-500 bg-green-50 hover:bg-green-100 hover:shadow-md'
                        }`}
                        data-testid="register-rol-estudiante"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <i className={`fas fa-user-graduate text-2xl ${
                            registerData.rol === 'estudiante' ? 'text-gray-700' : 'text-green-600'
                          }`}></i>
                          <span className={`text-sm font-medium ${
                            registerData.rol === 'estudiante' ? 'text-gray-800' : 'text-green-700'
                          }`}>
                            Estudiante
                          </span>
                          {registerData.rol === 'estudiante' && (
                            <i className="fas fa-check-circle text-gray-700 text-sm"></i>
                          )}
                        </div>
                      </button>

                      {/* Opción Pastor */}
                      <button
                        type="button"
                        onClick={() => setRegisterData({ ...registerData, rol: 'pastor' })}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          registerData.rol === 'pastor'
                            ? 'border-gray-800 bg-gray-100 shadow-lg ring-2 ring-gray-400'
                            : 'border-purple-500 bg-purple-50 hover:bg-purple-100 hover:shadow-md'
                        }`}
                        data-testid="register-rol-pastor"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <i className={`fas fa-church text-2xl ${
                            registerData.rol === 'pastor' ? 'text-gray-700' : 'text-purple-600'
                          }`}></i>
                          <span className={`text-sm font-medium ${
                            registerData.rol === 'pastor' ? 'text-gray-800' : 'text-purple-700'
                          }`}>
                            Pastor
                          </span>
                          {registerData.rol === 'pastor' && (
                            <i className="fas fa-check-circle text-gray-700 text-sm"></i>
                          )}
                        </div>
                      </button>

                      {/* Opción Profesor */}
                      <button
                        type="button"
                        onClick={() => setRegisterData({ ...registerData, rol: 'profesor' })}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          registerData.rol === 'profesor'
                            ? 'border-gray-800 bg-gray-100 shadow-lg ring-2 ring-gray-400'
                            : 'border-blue-500 bg-blue-50 hover:bg-blue-100 hover:shadow-md'
                        }`}
                        data-testid="register-rol-profesor"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <i className={`fas fa-chalkboard-teacher text-2xl ${
                            registerData.rol === 'profesor' ? 'text-gray-700' : 'text-blue-600'
                          }`}></i>
                          <span className={`text-sm font-medium ${
                            registerData.rol === 'profesor' ? 'text-gray-800' : 'text-blue-700'
                          }`}>
                            Profesor
                          </span>
                          {registerData.rol === 'profesor' && (
                            <i className="fas fa-check-circle text-gray-700 text-sm"></i>
                          )}
                        </div>
                      </button>

                      {/* Opción Graduado */}
                      <button
                        type="button"
                        onClick={() => setRegisterData({ ...registerData, rol: 'graduado' })}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          registerData.rol === 'graduado'
                            ? 'border-gray-800 bg-gray-100 shadow-lg ring-2 ring-gray-400'
                            : 'border-amber-500 bg-amber-50 hover:bg-amber-100 hover:shadow-md'
                        }`}
                        data-testid="register-rol-graduado"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <i className={`fas fa-award text-2xl ${
                            registerData.rol === 'graduado' ? 'text-gray-700' : 'text-amber-600'
                          }`}></i>
                          <span className={`text-sm font-medium ${
                            registerData.rol === 'graduado' ? 'text-gray-800' : 'text-amber-700'
                          }`}>
                            Graduado
                          </span>
                          {registerData.rol === 'graduado' && (
                            <i className="fas fa-check-circle text-gray-700 text-sm"></i>
                          )}
                        </div>
                      </button>
                    </div>
                    
                    {/* Mensaje informativo para graduados */}
                    {registerData.rol === 'graduado' && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <i className="fas fa-info-circle text-amber-600 mt-0.5"></i>
                          <p className="text-sm text-amber-700">
                            Si eres egresado del Instituto Bíblico Apostólico, selecciona esta opción. 
                            Tu cuenta será verificada por el administrador para darte acceso al área VIP de graduados.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full instituto-gradient btn-hover text-white font-medium py-3 mt-6"
                    disabled={loading}
                    data-testid="register-submit-button"
                  >
                    <span className="flex items-center justify-center">
                      {loading ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                          <span>Creando cuenta...</span>
                        </>
                      ) : (
                        <>
                          <span>Crear Cuenta</span>
                          <i className="fas fa-user-plus ml-2"></i>
                        </>
                      )}
                    </span>
                  </Button>

                  {/* Link de formulario adicional para estudiantes */}
                  {registroConfig && registerData.rol === 'estudiante' && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <i className="fas fa-info-circle text-blue-600 mt-1"></i>
                        <div className="flex-1">
                          <h4 className="font-semibold text-blue-800 mb-1">
                            {registroConfig.titulo}
                          </h4>
                          <p className="text-sm text-blue-700 mb-3">
                            {registroConfig.descripcion}
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(registroConfig.link_registro, '_blank')}
                            className="border-blue-300 text-blue-700 hover:bg-blue-100"
                          >
                            <i className="fas fa-external-link-alt mr-2"></i>
                            Completar Formulario
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            <i className="fas fa-shield-alt mr-2"></i>
            Plataforma segura del Instituto Bíblico Apostólico
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
